import create from 'zustand';
import { Nullable } from 'src/types/nullable.type';
import { Node } from 'src/common/interfaces/node.interface';
import { Destination } from 'src/common/interfaces/destination.interface';

import { LibraryTabType, LIBRARY_TABS } from 'src/editor/step-editor/content/library/types';

import isEqual from 'lodash/isEqual';
import { VideoAsset } from 'src/common/interfaces/video-asset.interface';
import { ImageAsset } from 'src/common/interfaces/image-asset.interface';
import { FileAsset } from 'src/common/interfaces/file-asset.interface';
import { Document } from 'src/common/interfaces/document.interface';

import {
  CONTENT_TYPE_VIDEO,
  CONTENT_TYPE_IMAGE,
  CONTENT_TYPE_PDF,
  CONTENT_TYPE_EXTERNAL_LINK,
  CONTENT_TYPE_FILE,
  CONTENT_TYPE_EMBED,
  CONTENT_TYPE_JOURNEY_DOCUMENT,
  STEP_EDITOR_CONTENT_OPTION_URL,
  STEP_EDITOR_CONTENT_OPTION_EXISTING_CONTENT,
  STEP_EDITOR_CONTENT_OPTION_RECORD,
  STEP_EDITOR_CONTENT_OPTION_UPLOAD,
  STEP_EDITOR_CONTENT_OPTION_LIBRARY,
  STEP_EDITOR_CONTENT_OPTION_INITIAL,
  STEP_EDITOR_CONTENT_OPTION_JOURNEY_DOCUMENT,
  CONTENT_BEHAVIOR_DISPLAY_IN_JOURNEY,
  CONTENT_BEHAVIOR_EXTERNAL_LINK,
  ANNOTATION_LINK_BEHAVIOR_EXTERNAL,
  ANNOTATION_LINK_BEHAVIOR_STEP,
} from 'src/editor/constants';
import { getNodeAnnotation } from 'src/common/helpers/annotation';
import { isValidUrl } from 'src/utils/url';
import { StepEditorContentConfigureStageTypeEnum } from 'src/editor/step-editor/content/configure/stage-type.enum';
import { ContentTypeEnum } from 'src/common/interfaces/content/type.enum';

const MAX_NAME_LENGTH = 100;

export interface ExternalLinkProps {
  id: Nullable<number>;
  title: string;
  description: string;
  cta: string;
  meta_image_url: string;
  hide_image: boolean;
}

export interface AnnotationFormProps {
  id: Nullable<number>;
  text: string;
  deleted: boolean;
  hide?: boolean;
  link?: AnnotationLinkProps;
}

export type AnnotationLinkBehavior = Nullable<
  typeof ANNOTATION_LINK_BEHAVIOR_EXTERNAL | typeof ANNOTATION_LINK_BEHAVIOR_STEP | ''
>;

export type ContentBehavior = typeof CONTENT_BEHAVIOR_DISPLAY_IN_JOURNEY | typeof CONTENT_BEHAVIOR_EXTERNAL_LINK;

export interface AnnotationLinkProps {
  behavior: AnnotationLinkBehavior;
  linked_step_id: Nullable<number>;
  linked_step_label: Nullable<string>;
  url: Nullable<string>;
  title: Nullable<string>;
}

interface StateVars {
  node_id: Nullable<number>;
  name: string;
  url: string;
  transformed_url: string;
  embed_code: string;
  searchQuery: string;
  link: ExternalLinkProps;
  video_asset: Nullable<VideoAsset>;
  image_asset: Nullable<ImageAsset>;
  file_asset: Nullable<FileAsset>;
  document: Nullable<Document>;
  destination: Nullable<Destination>;
  thumbnail: Nullable<ImageAsset>;
  is_synced: boolean;
  is_email_required: boolean;
  isPlaceholder: Nullable<true>;
  placeholderNodeId: Nullable<number>;
  contentOption: string;
  contentType: string;
  oldContentName: string;
  contentBehavior: string;
  contentName: string;
  loading: boolean;
  warningMessage: Nullable<string>;
  previewNode: Nullable<Node>;
  previewObject: Nullable<any>;
  previewVideoHTML: Nullable<any>;
  isMobilePreview: boolean;
  oldContentBehavior: string;
  isEmbeddable: boolean;
  annotation: any; // TODO correct the types for annotation. this should be union of form & server object
  isAnnotationEnabled: boolean;
  currentConfigurationStage: StepEditorContentConfigureStageTypeEnum;
  libraryTab: LibraryTabType;
  uploadedFile: Nullable<File>;
  uploadedFileParams: any;
  uploadedS3Asset: any;
  uploadInProgress: boolean;
  uploadPercentage: number;
  optimizeFullscreenMobile: boolean;
}

const initialState: StateVars = {
  node_id: null,
  name: '',
  url: '',
  transformed_url: '',
  embed_code: '',
  searchQuery: '',
  // TODO: rename to link properties
  link: {
    id: null,
    title: '',
    description: '',
    cta: '',
    meta_image_url: '',
    hide_image: false,
  },
  video_asset: null,
  image_asset: null,
  file_asset: null,
  document: null,
  annotation: { id: null, text: '', deleted: false },
  isAnnotationEnabled: false,
  isPlaceholder: null,
  placeholderNodeId: null,
  is_synced: false,
  is_email_required: false,
  libraryTab: LIBRARY_TABS[0].type,
  destination: null,
  thumbnail: null,
  contentOption: STEP_EDITOR_CONTENT_OPTION_INITIAL,
  contentType: CONTENT_TYPE_EMBED,
  oldContentName: '',
  contentName: '',
  currentConfigurationStage: StepEditorContentConfigureStageTypeEnum.BOARD,
  oldContentBehavior: CONTENT_BEHAVIOR_DISPLAY_IN_JOURNEY,
  contentBehavior: CONTENT_BEHAVIOR_DISPLAY_IN_JOURNEY,
  loading: false,
  warningMessage: null,
  previewObject: null,
  previewNode: null,
  previewVideoHTML: null,
  isMobilePreview: false,
  isEmbeddable: false,
  uploadedFile: null,
  uploadedFileParams: null,
  uploadedS3Asset: null,
  uploadInProgress: false,
  uploadPercentage: 0,
  optimizeFullscreenMobile: false,
};

interface StateFns {
  setName: (name: string) => void;
  setUrl: (url: string) => void;
  setTransformedUrl: (transformedUrl: string) => void;
  setEmbedCode: (embedCode: string) => void;
  setSearchQuery: (searchQuery: string) => void;
  setLink: (link: ExternalLinkProps) => void;
  setDestination: (destination: any) => void;
  setContentOption: (contentOption: string) => void;
  setContentType: (contentType: string) => void;
  setContentBehavior: (contentBehavior: string) => void;
  setLoading: (loading: boolean) => void;
  setWarningMessage: (warningMessage: Nullable<string>) => void;
  setPreviewObject: (previewObject: Nullable<any>) => void;
  setPreviewNode: (previewNode: Nullable<Node>) => void;
  setPreviewVideoHTML: (previewVideoHTML: Nullable<any>) => void;
  setCurrentConfigurationStage: (stage: StepEditorContentConfigureStageTypeEnum) => void;
  setIsMobilePreview: (isMobilePreview: boolean) => void;
  setIsEmbeddable: (isEmbeddable: boolean) => void;
  setAnnotation: (annotation: AnnotationFormProps) => void;
  setContentName: (value: string) => void;
  setOldContentName: (value: string) => void;
  setAnnotationLinkProps: (link: any) => void;
  updateAnnotation: (params: any) => void; // TODO: enforce keys of AnnotationProps
  setAnnotationLinkBehavior: (behavior: AnnotationLinkBehavior) => void;
  setOldContentBehavior: (contentBehavior: string) => void;
  setLibraryTab: (libraryTab: LibraryTabType) => void;
  setUploadedFile: (uploadedFile: Nullable<File>) => void;
  setUploadedFileParams: (uploadedFileParams: any) => void;
  setUploadedS3Asset: (uploadedS3Asset: any) => void;
  setUploadInProgress: (uploadInProgress: boolean) => void;
  setUploadPercentage: (uploadPercentage: number) => void;
  setOptimizeFullscreenMobile: (optimizeFullscreenMobile: boolean) => void;
  setDocument: (document: Nullable<Document>) => void;
  setContentFromNode: (node: Node) => void;
  getContentFromNode: (node: Node) => any;
  getStateFromNode: (node: Node) => any;
  setStateFromNode: (node: Node) => void;
  isButtonVisible: () => boolean;
  isButtonDisabled: () => boolean;
  isAnnotationsVisible: () => boolean;
  isContentBehaviorVisible: () => boolean;
  isAnnotationFormValid: () => boolean;
  hasChanges: (node: Node) => boolean;
  resetLink: () => void;
  resetContent: () => void;
  resetOldContentName: () => void;
  resetOldContentBehavior: () => void;
  resetUploader: () => void;
  resetStepEditor: (values?: Partial<StateVars>) => void;
}

type State = StateVars & StateFns;

export const useStepEditorContext = create<State>((set, get) => ({
  ...initialState,
  setName: (name: string) => {
    set({ name: name?.substring(0, MAX_NAME_LENGTH) });
  },
  setUrl: (url: string) => {
    set(() => ({
      url: url,
    }));
  },
  setTransformedUrl: (transformedUrl: string) => {
    set(() => ({
      transformed_url: transformedUrl,
    }));
  },
  setEmbedCode: (embedCode: string) => {
    set(() => ({
      embed_code: embedCode,
    }));
  },
  setSearchQuery: (searchQuery: string) => {
    set(() => ({
      searchQuery: searchQuery,
    }));
  },
  setLink: (link: ExternalLinkProps) => {
    set(() => ({
      link: link,
    }));
  },
  setDestination: (destination: any) => {
    set({ destination });
  },
  setContentOption: (contentOption: string) => {
    set({ contentOption });
  },
  setContentType: (contentType: string) => {
    set({ contentType });
  },
  setContentBehavior: (contentBehavior: string) => {
    set({ contentBehavior });
  },
  setLoading: (loading: boolean) => {
    set({ loading });
  },
  setWarningMessage: (warningMessage: Nullable<string>) => {
    set({ warningMessage });
  },
  setPreviewObject: (previewObject: Nullable<any>) => {
    set({ previewObject });
  },
  setPreviewNode: (previewNode: Nullable<Node>) => {
    set({ previewNode });
  },
  setPreviewVideoHTML: (previewVideoHTML: Nullable<any>) => {
    set({ previewVideoHTML });
  },
  setIsMobilePreview: (isMobilePreview: boolean) => {
    set({ isMobilePreview });
  },
  setIsEmbeddable: (isEmbeddable: boolean) => {
    set({ isEmbeddable });
  },
  setAnnotation: (annotation: AnnotationFormProps) => {
    set({ annotation, isAnnotationEnabled: Boolean(annotation.text) || !annotation.deleted });
  },
  setAnnotationLinkBehavior: (behavior: AnnotationLinkBehavior) => {
    const link: AnnotationLinkProps = {
      linked_step_id: null,
      linked_step_label: null,
      url: null,
      title: null,
      behavior,
    };
    set({ annotation: { ...get().annotation, link, linked_step: null, external_link: null } });
  },
  setAnnotationLinkProps: (props: any) => {
    const annotation = get().annotation;
    set({ annotation: { ...annotation, link: { ...annotation.link, ...props } } });
  },
  updateAnnotation: (params: any) => {
    const annotation = { ...get().annotation, ...params };
    set({ annotation, isAnnotationEnabled: annotation.text || !annotation.deleted });
  },
  setLibraryTab: (libraryTab: LibraryTabType) => {
    set({ libraryTab });
  },
  setUploadedFile: (uploadedFile: Nullable<File>) => {
    if (uploadedFile) {
      if (uploadedFile.type.startsWith('video')) {
        set({ contentType: CONTENT_TYPE_VIDEO });
      } else if (uploadedFile.type.startsWith('image')) {
        set({ contentType: CONTENT_TYPE_IMAGE });
      } else if (uploadedFile.type.endsWith('pdf')) {
        set({ contentType: CONTENT_TYPE_PDF });
      } else {
        set({ contentType: CONTENT_TYPE_FILE });
      }
    } else {
      set({ contentType: '' });
    }
    set({ uploadedFile });
  },
  setUploadedFileParams: (uploadedFileParams: any) => {
    set({ uploadedFileParams });
  },
  setCurrentConfigurationStage: (currentConfigurationStage) => {
    set({ currentConfigurationStage });
  },
  setUploadedS3Asset: (uploadedS3Asset: any) => {
    set({ uploadedS3Asset });
  },
  setUploadInProgress: (uploadInProgress: boolean) => {
    set({ uploadInProgress });
  },
  setUploadPercentage: (uploadPercentage: number) => {
    set({ uploadPercentage });
  },
  setOptimizeFullscreenMobile: (optimizeFullscreenMobile: boolean) => {
    set({ optimizeFullscreenMobile });
  },
  setDocument: (document: any) => {
    set({ document });
  },
  setContentName(value) {
    set({ contentName: value });
  },
  setOldContentName(value) {
    set({ oldContentName: value });
  },
  setContentFromNode: (node: Node) => {
    set((state: any) => ({
      ...state,
      ...get().getContentFromNode(node),
    }));
  },
  getContentFromNode: (node: Node) => {
    let contentParams: any = {};

    if (node.embed) {
      contentParams = {
        contentOption: STEP_EDITOR_CONTENT_OPTION_URL,
        contentType: CONTENT_TYPE_EMBED,
        contentBehavior: CONTENT_BEHAVIOR_DISPLAY_IN_JOURNEY,
        contentName: node.name,
        destination: node.embed.destination,
        url: node.embed.url,
        transformed_url: node.embed.transformed_url,
        embed_code: node.embed.embed_code,
      };
    } else if (node.video_asset) {
      contentParams = {
        contentOption: STEP_EDITOR_CONTENT_OPTION_EXISTING_CONTENT,
        contentType: CONTENT_TYPE_VIDEO,
        contentBehavior: CONTENT_BEHAVIOR_DISPLAY_IN_JOURNEY,
        contentName: node.video_asset.name,
        video_asset: node.video_asset,
      };
    } else if (node.file_asset) {
      if (node.content_type === ContentTypeEnum.FILE) {
        contentParams = {
          contentOption: STEP_EDITOR_CONTENT_OPTION_EXISTING_CONTENT,
          contentType: node.content_type,
          contentBehavior: CONTENT_BEHAVIOR_EXTERNAL_LINK,
          contentName: node.file_asset.name,
          file_asset: node.file_asset,
          link: {
            title: node.file_asset.data.link_title,
            description: node.file_asset.data.link_description,
            hide_image: node.file_asset.data.hide_image,
          },
        };
      } else {
        contentParams = {
          contentOption: STEP_EDITOR_CONTENT_OPTION_EXISTING_CONTENT,
          contentType: node.content_type,
          contentBehavior: CONTENT_BEHAVIOR_DISPLAY_IN_JOURNEY,
          contentName: node.file_asset.name,
          file_asset: node.file_asset,
        };
      }
    } else if (node.external_link) {
      contentParams = {
        contentOption: STEP_EDITOR_CONTENT_OPTION_URL,
        contentType: CONTENT_TYPE_EXTERNAL_LINK,
        contentBehavior: CONTENT_BEHAVIOR_EXTERNAL_LINK,
        contentName: node.external_link.title,
        url: node.external_link.url,
        link: {
          ...node.external_link,
          id: node.external_link.id,
          title: node.external_link.title,
          description: node.external_link.description,
          cta: node.external_link.cta,
          meta_image_url: node.external_link.thumbnail_desktop_asset?.url || node.external_link.meta_image_url,
          hide_image: !node.external_link.meta_image_url,
        },
      };
    } else if (node.image_asset) {
      contentParams = {
        contentOption: STEP_EDITOR_CONTENT_OPTION_EXISTING_CONTENT,
        contentType: CONTENT_TYPE_IMAGE,
        contentBehavior: CONTENT_BEHAVIOR_DISPLAY_IN_JOURNEY,
        contentName: node.image_asset.name,
        image_asset: node.image_asset,
      };
    } else if (node.document) {
      contentParams = {
        contentOption: STEP_EDITOR_CONTENT_OPTION_JOURNEY_DOCUMENT,
        contentType: CONTENT_TYPE_JOURNEY_DOCUMENT,
        contentBehavior: null,
        contentName: node.name,
        document: node.document,
      };
    }

    return {
      ...contentParams,
      previewNode: node,
    };
  },
  getStateFromNode: (node: Node) => {
    const contentParams = get().getContentFromNode(node);
    const stepParams = {
      name: node.name,
      node_id: node.id,
    };
    const annotation = getNodeAnnotation(node);
    const annotationParams = {
      annotation,
      isAnnotationEnabled: annotation && (annotation.text || !annotation.deleted),
    };

    const previewParams = {
      previewNode: node,
    };

    return {
      // ...initialState,
      url: initialState.url,
      transformed_url: initialState.transformed_url,
      embed_code: initialState.embed_code,
      searchQuery: initialState.searchQuery,
      link: initialState.link,
      video_asset: initialState.video_asset,
      image_asset: initialState.image_asset,
      file_asset: initialState.file_asset,
      document: initialState.document,
      isMobilePreview: initialState.isMobilePreview,
      is_synced: node.is_synced,
      is_email_required: node.is_email_required,
      thumbnail: node.thumbnail,
      isPlaceholder: false,
      placeholderNodeId: null,
      ...stepParams,
      ...contentParams,
      ...annotationParams,
      ...previewParams,
      // previewObject: { node: node },
    };
  },
  setStateFromNode: (node: Node) => {
    set((state: any) => ({
      // contentType: node.content_type,
      ...get().getStateFromNode(node),
      // previewObject: { node },
    }));
  },
  isButtonVisible: (): boolean => {
    const { contentOption, url, uploadedFile } = get();
    if (contentOption === STEP_EDITOR_CONTENT_OPTION_URL && url) return true;
    if (contentOption === STEP_EDITOR_CONTENT_OPTION_RECORD && uploadedFile) return true;
    if (contentOption === STEP_EDITOR_CONTENT_OPTION_UPLOAD && uploadedFile) return true;
    if (contentOption === STEP_EDITOR_CONTENT_OPTION_EXISTING_CONTENT) return true;
    if (contentOption === STEP_EDITOR_CONTENT_OPTION_JOURNEY_DOCUMENT) return true;

    return false;
  },
  isButtonDisabled: (): boolean => {
    const { contentOption, url, loading, uploadInProgress } = get();
    if (contentOption === STEP_EDITOR_CONTENT_OPTION_URL && !url) return true;
    if (contentOption === STEP_EDITOR_CONTENT_OPTION_URL && loading) return true;
    if (contentOption === STEP_EDITOR_CONTENT_OPTION_RECORD && uploadInProgress) return true;
    if (contentOption === STEP_EDITOR_CONTENT_OPTION_UPLOAD && uploadInProgress) return true;

    return false;
  },
  isAnnotationsVisible: (): boolean => {
    const { node_id, contentOption, url, loading, uploadedFile } = get();

    if (contentOption === STEP_EDITOR_CONTENT_OPTION_URL && (!url || loading)) return false;
    if (contentOption === STEP_EDITOR_CONTENT_OPTION_RECORD && !uploadedFile) return false;
    if (contentOption === STEP_EDITOR_CONTENT_OPTION_UPLOAD && !uploadedFile && !node_id) return false;
    if (contentOption === STEP_EDITOR_CONTENT_OPTION_LIBRARY) return false;

    return true;
  },
  isAnnotationFormValid: (): boolean => {
    const { annotation } = get();
    if (!annotation || annotation.deleted) return true;
    const { link }: AnnotationFormProps = annotation;
    if (!link) return true;
    const linkFormValid =
      link &&
      ((link.behavior === ANNOTATION_LINK_BEHAVIOR_EXTERNAL &&
        Boolean(link.url) &&
        isValidUrl(link.url) &&
        Boolean(link.title)) ||
        (link.behavior === ANNOTATION_LINK_BEHAVIOR_STEP &&
          Boolean(link.linked_step_id) &&
          Boolean(link.linked_step_label)));

    return Boolean(annotation.text) && linkFormValid;
  },
  isContentBehaviorVisible: (): boolean => {
    const { contentOption, url, loading } = get();

    if (contentOption === STEP_EDITOR_CONTENT_OPTION_URL && url && !loading) return true;

    return false;
  },
  hasChanges: (currentNode: Node): boolean => {
    const params = [
      'name',
      'url',
      'embed_code',
      'link',
      'destination',
      'video_asset',
      'image_asset',
      'file_asset',
      'contentOption',
      'contentType',
      'contentBehavior',
      'annotation',
      'uploadedS3Asset',
      'is_synced',
      'is_email_required',
    ];

    const state: any = get();

    let stateToCompare: any = {};
    if (currentNode) {
      stateToCompare = state.getStateFromNode(currentNode);
    } else {
      stateToCompare = initialState;
    }
    for (const param of params) {
      if (!isEqual(state[param], stateToCompare[param])) {
        return true;
      }
    }
    return false;
  },
  resetLink: () => {
    set({
      link: initialState.link,
    });
  },
  setOldContentBehavior: (behavior) => {
    set({ oldContentBehavior: behavior });
  },
  resetOldContentBehavior: () => {
    set({ oldContentBehavior: CONTENT_BEHAVIOR_DISPLAY_IN_JOURNEY });
  },
  resetContent: () => {
    const { oldContentName } = get();
    // this will be replaced with set({ content: initialState.content }) soon.
    set({
      url: initialState.url,
      transformed_url: initialState.transformed_url,
      embed_code: initialState.embed_code,
      destination: initialState.destination,
      video_asset: initialState.video_asset,
      image_asset: initialState.image_asset,
      file_asset: initialState.file_asset,
      link: initialState.link,
      contentName: oldContentName || '',
      previewObject: null,
      previewNode: null,
    });
  },
  resetOldContentName() {
    set({ oldContentName: '' });
  },
  resetStepEditor: (values) => {
    set(() => ({
      ...initialState,
      ...(values || {}),
    }));
  },
  resetUploader: () => {
    set({
      uploadedFile: null,
      uploadedFileParams: null,
      uploadedS3Asset: null,
      uploadInProgress: false,
      uploadPercentage: 0,
    });
  },
}));
